import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { generatePromptsForImageV1ImagesImageIdGeneratePromptsPost } from '../../generated';
import type { GeneratePromptsForImageV1ImagesImageIdGeneratePromptsPostData } from '../../generated';
import type { GeneratePromptsForImageV1ImagesImageIdGeneratePromptsPostError } from '../../generated';
import type { GeneratePromptsForImageV1ImagesImageIdGeneratePromptsPostResponse } from '../../generated';

/**
 * Longest prompt the endpoint accepts; anything longer is rejected with 422.
 * The spec carries this as `maxLength` but hey-api drops constraints, so this
 * is a second copy of the API's own USER_PROMPT_MAX_LENGTH. Raising the cap
 * means changing both.
 */
const GENERATE_PROMPT_MAX_LENGTH = 1000;

/**
 * Variables for the generate-prompts mutation: the target image's path params
 * plus the optional `prompt` that steers the next batch.
 */
export type GeneratePromptsForImageMutationVariables =
  GeneratePromptsForImageV1ImagesImageIdGeneratePromptsPostData['path'] & {
    /**
     * What the next generation batch should change about the source image.
     * On a first round it is sent alone, under the endpoint's default mode,
     * which steers the round's step 1. Once a round exists it is sent as
     * `generation_mode: "generate_more"` plus this text. Omitted, empty or
     * whitespace sends no body at all, which is the plain "generate more" the
     * endpoint defaults to. Capped at 1000 characters by the API.
     *
     * @optional
     */
    prompt?: string;
    /**
     * Whether the target is itself a generated image. Those only accept
     * `generate_more`, so the mode is sent even with no prompt; a root keeps
     * sending no body at all.
     *
     * @optional
     */
    isRemix?: boolean;
    /**
     * Whether the target already has a completed round to iterate on. Decides
     * which mode a prompted press names: `generate_more` when there is one,
     * the default mode when the press starts the first round. Ignored for a
     * generated image, which always names `generate_more`.
     *
     * @optional
     * @default false
     */
    hasPriorRound?: boolean;
  };

/** Response of the generate-prompts endpoint, unchanged from the generated client. */
export type GeneratePromptsForImageMutation = GeneratePromptsForImageV1ImagesImageIdGeneratePromptsPostResponse;

const requestOptions = ({
  image_id,
  prompt,
  isRemix = false,
  hasPriorRound = false,
}: GeneratePromptsForImageMutationVariables) => {
  const direction = prompt?.trim();
  // Omitting `body` entirely keeps the unprompted request identical to the one
  // sent before prompts existed, which is what the endpoint's default relies on.
  // Whitespace counts as unprompted: sending it would pick a mode whose
  // preconditions run before the backend collapses the blank to no prompt.
  // A generated target is the exception, since its default mode is refused.
  // A prompted first round on a root names no mode: generate_more 422s without
  // a round to build on, and the default mode steers the first round itself.
  const iteratesOnARound = isRemix || hasPriorRound;
  const body = direction
    ? iteratesOnARound
      ? { generation_mode: 'generate_more' as const, prompt: direction }
      : { prompt: direction }
    : isRemix
      ? { generation_mode: 'generate_more' as const }
      : undefined;

  return {
    path: { image_id },
    ...(body ? { body } : {}),
    throwOnError: true as const,
  };
};

export const useGeneratePromptsForImageMutation = <
  TError = GeneratePromptsForImageV1ImagesImageIdGeneratePromptsPostError,
  TContext = unknown,
>(
  options?: Omit<
    UseMutationOptions<GeneratePromptsForImageMutation, TError, GeneratePromptsForImageMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<GeneratePromptsForImageMutation, TError, GeneratePromptsForImageMutationVariables, TContext>({
    mutationKey: ['GeneratePromptsForImage'],
    mutationFn: async (variables: GeneratePromptsForImageMutationVariables) => {
      const { data } = await generatePromptsForImageV1ImagesImageIdGeneratePromptsPost(requestOptions(variables));
      return data;
    },
    ...options,
  });
};

useGeneratePromptsForImageMutation.fetcher = (
  variables: GeneratePromptsForImageMutationVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await generatePromptsForImageV1ImagesImageIdGeneratePromptsPost(requestOptions(variables));
    return data;
  };
};

export { GENERATE_PROMPT_MAX_LENGTH };
